/**
 * 完整 7 段报告面板
 *
 * 流程：
 *   1. 用户进入此面板时已选定流派（calibrationId 必填）
 *   2. 自动开始 SSE 流式生成
 *   3. 后端无权益时返回 PAYWALL_REQUIRED → 弹付费墙
 *   4. 用户可选择单次解锁或开通会员；有权益时完整展示
 *
 * 进度仅用于展示生成状态，不再用于试看/自动拦截。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PaywallConfigView } from '@guanwei/shared';
import { Button } from '@/components/ui/Button';
import { streamFullReport } from '@/lib/calibration.api';
import { fetchPaywall } from '@/lib/business.api';
import { ReportPaywallModal } from '@/components/paywall/ReportPaywallModal';
import { AnalyzingOverlay } from '@/components/premium/AnalyzingOverlay';
import {
  InsightCard,
  PremiumReportRenderer,
  ReportPaper,
  parseReportSegments,
} from '@/components/premium/ReportPaper';

const PAYWALL_SCENE = 'report.generating';
const FALLBACK_PAYWALL_CONFIG: PaywallConfigView = {
  scene: PAYWALL_SCENE,
  enabled: true,
  triggerProgress: 0,
  previewSections: ['格局定调', '天赋与性格'],
  ctas: {
    primary: { label: '单次解锁完整报告 ¥19.9', productCode: 'report.full.single' },
  },
  copy: {
    title: '解锁完整命盘报告',
    subtitle: '继续查看财富、事业、关系、健康、流年与行动清单等完整内容。',
    hookList: ['单次购买解锁当前命盘', '支付后本命盘可重复查看', '也可以开通会员获取更多权益'],
  },
  variants: null,
};

const TOTAL_SECTIONS = 7;

interface FullReportPanelProps {
  chartId: string;
  calibrationId: string;
  onReportStart?: () => void;
  onReportDone?: (reportId?: string, content?: string) => void;
}

type Phase = 'idle' | 'streaming' | 'done' | 'error';

function isPaywallError(message: string | null | undefined) {
  if (!message) return false;
  return message.includes('PAYWALL_REQUIRED') || message.includes('完整报告需要购买');
}

export function FullReportPanel({
  chartId,
  calibrationId,
  onReportStart,
  onReportDone,
}: FullReportPanelProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [paywallConfig, setPaywallConfig] = useState<PaywallConfigView>(FALLBACK_PAYWALL_CONFIG);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const abortRef = useRef<{ abort: () => void } | null>(null);
  const contentRef = useRef('');

  // 初次加载付费墙配置；数据库未初始化时使用兜底购买入口，避免用户只看到失败文字。
  useEffect(() => {
    void fetchPaywall(PAYWALL_SCENE)
      .then((config) => setPaywallConfig(config ?? FALLBACK_PAYWALL_CONFIG))
      .catch(() => setPaywallConfig(FALLBACK_PAYWALL_CONFIG));
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const start = () => {
    setPhase('streaming');
    setContent('');
    contentRef.current = '';
    setError(null);
    onReportStart?.();
    abortRef.current = streamFullReport(chartId, calibrationId, {
      onChunk: (delta) => {
        contentRef.current += delta;
        setContent(contentRef.current);
      },
      onDone: (reportId) => {
        setPhase('done');
        onReportDone?.(reportId, contentRef.current);
      },
      onError: (msg) => {
        setError(msg);
        setPhase('error');
        if (isPaywallError(msg)) {
          setPaywallOpen(true);
        }
      },
    });
  };

  // 自动启动一次
  useEffect(() => {
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, calibrationId]);

  // 解析为段落
  const segments = useMemo(() => parseReportSegments(content), [content]);

  // 生成进度（基于已出现的二级标题数）
  const progress = useMemo(() => {
    const h2Count = segments.filter((s) => s.type === 'h2').length;
    if (h2Count === 0) return Math.min(8, Math.floor(content.length / 50));
    return Math.min(100, Math.round((h2Count / TOTAL_SECTIONS) * 100));
  }, [segments, content.length]);

  return (
    <ReportPaper
      title="完整 7 段战略报告"
      subtitle="基于已锁定流派生成财富、事业、关系、流年与行动清单。"
      progress={progress}
    >
      {phase === 'error' && !isPaywallError(error) && (
        <InsightCard title="报告生成暂时失败">
          <p>{error ?? '请稍后重试。'}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={start}>
            重试
          </Button>
        </InsightCard>
      )}

      {phase === 'error' && isPaywallError(error) && (
        <InsightCard title="完整报告待解锁">
          <p>{error ?? '完整报告需要购买单次解锁或开通会员。'}</p>
          <Button size="sm" className="mt-3" onClick={() => setPaywallOpen(true)}>
            查看解锁方式
          </Button>
        </InsightCard>
      )}

      {phase === 'streaming' && !content && <AnalyzingOverlay />}

      {(content || phase === 'done') && (
        <div className="space-y-4">
          <PremiumReportRenderer segments={segments} streaming={phase === 'streaming'} />
        </div>
      )}

      <ReportPaywallModal
        open={paywallOpen}
        scene={PAYWALL_SCENE}
        config={paywallConfig}
        fulfillmentMeta={{ chartId, calibrationId }}
        onClose={() => setPaywallOpen(false)}
        onUnlocked={() => setPaywallOpen(false)}
        dismissText="我再考虑一下"
      />
    </ReportPaper>
  );
}
